/**
 * =============================================================================
 * Hook: useDefaultAffiliateProgram
 * Versão: 1.0.0
 *
 * Descrição:
 *   Resolve o programa de indicação PADRÃO de um tenant a partir do `slug`
 *   público do tenant. Usado por:
 *     - AffiliateSignupResolver (rota /afiliado/<slug>/cadastro) para
 *       redirecionar o visitante para a landing do programa padrão.
 *     - AffiliateBrandingTab para alertar o admin quando ainda não há
 *       programa padrão configurado.
 *
 *   Reusa a RPC pública `get_default_affiliate_program_by_tenant_slug`
 *   (SECURITY DEFINER, RAISE EXCEPTION quando não encontrado).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('useDefaultAffiliateProgram');

export interface DefaultAffiliateProgramResult {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  program_id: string;
  program_slug: string;
  program_name: string;
}

/**
 * Busca o programa padrão para um tenantSlug. Retorna `null` quando não há
 * (RPC retorna erro `no_data_found`), permitindo a UI tratar com mensagem
 * amigável sem soltar exceção no console.
 */
export function useDefaultAffiliateProgram(tenantSlug: string | null | undefined) {
  return useQuery<DefaultAffiliateProgramResult | null>({
    queryKey: ['default-affiliate-program', tenantSlug],
    enabled: !!tenantSlug,
    staleTime: 60_000,
    queryFn: async () => {
      const slug = (tenantSlug ?? '').trim().toLowerCase();
      if (!slug) return null;

      const { data, error } = await (supabase.rpc as any)(
        'get_default_affiliate_program_by_tenant_slug',
        { p_tenant_slug: slug },
      );

      if (error) {
        // Sem programa padrão configurado é cenário esperado — não polui o log
        if (
          error.code === 'P0001' ||
          /nenhum programa padr[ãa]o/i.test(error.message ?? '')
        ) {
          return null;
        }
        log.error('Erro ao resolver programa padrão', error);
        throw error;
      }

      const row = Array.isArray(data) ? data[0] : data;
      return (row ?? null) as DefaultAffiliateProgramResult | null;
    },
  });
}
